<script setup>
import {
  computed,
  ref,
  inject,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  useId,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelineCard from './PipelineCard.vue';
import PipelineFilter from './PipelineFilter.vue';
import Modal from '../../../../components/Modal.vue';
import StageColorPicker from './StageColorPicker.vue';
import MarkAsWonModal from './MarkAsWonModal.vue';
import MarkAsLostModal from './MarkAsLostModal.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import { isSet } from 'dashboard/helper/filterHelper';
import { emitter } from 'shared/helpers/mitt';
import {
  matchesColumnFilters,
  getCardValue,
  getCardPriority,
} from '../helpers/columnFilterMatchers';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: '#64748b',
  },
  items: {
    type: Array,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
});
const emit = defineEmits([
  'add',
  'edit',
  'delete',
  'drop',
  'itemClick',
  'itemsUpdated',
  'cardStageChanged',
  'hideToggle',
]);

// Note: Filters are now loaded in onMounted() with user-scoped keys
// to ensure user isolation when switching between accounts

const { t } = useI18n();

const store = useStore();

// Ciclo 9 (a11y) — prefixo único por instância para os ids dos controles dos
// modais de ordenação/edição e do menu de opções: o componente monta uma vez
// POR COLUNA, então ids fixos duplicariam no DOM e quebrariam label↔campo.
const uid = useId();
const fieldId = suffix => `${uid}-${suffix}`;

// User-scoped localStorage key prefix to isolate data between users
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const userScopedKeyPrefix = computed(
  () => `u${currentUserId.value}_a${currentAccountId.value}_`
);

// Verificar se etapas ganho/perdido estão habilitadas
const enableWonLostStages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  return pipeline?.settings?.enable_won_lost_stages !== false;
});

// Adicionar estado para filtros
const showFilterModal = ref(false);
// Initialize columnFilters - will be loaded from localStorage in onMounted()
const columnFilters = ref(null);
const showOptionsMenu = ref(false);
const columnSettings = ref({
  showWon: true,
  showLost: true,
  showAll: true,
  hideColumn: false,
});
const showEditStageModal = ref(false);
const editingStageForm = ref({
  name: '',
  color: '',
  description: '',
});
// Adicionar ref para controle do modal de ordenação
const showSortModal = ref(false);
const columnSort = ref({
  // Default: ordem manual (position). Mantém os cards onde o usuário os
  // soltou no drag-and-drop e reflete o reorder em tempo real. `position`
  // é NOT NULL (default via set_default_position = max+1 por stage), então
  // a ordenação é estável; cards novos são anexados ao fim do estágio.
  type: 'position',
  direction: 'asc',
});
// Colapso em massa: injeta o ref reativo provido pelo PipelineView (fonte
// única que os cards leem) + setter em lote. O computed derivado
// (allItemsCollapsed) é declarado após filteredItems.
const collapsedItemsShared = inject('collapsedItems', ref({}));
const updateCollapsedItems = inject('updateCollapsedItems', () => {});

// Refs para Lazy Loading
const itemsToShowCount = ref(6); // Mostrar 6 itens inicialmente
const itemsIncrement = 10; // Carregar mais 10 a cada vez
const scrollContainer = ref(null); // Ref para o container de scroll

// Scroll indicator state
const canScrollDown = ref(false);
const updateScrollIndicator = () => {
  if (!scrollContainer.value) return;
  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  canScrollDown.value = scrollHeight - scrollTop - clientHeight > 20;
};

// Drag & Drop state
const isDragOver = ref(false);

// Opções de ordenação disponíveis
const sortOptions = computed(() => [
  { id: 'position', label: t('PIPELINE.SORT_OPTIONS.POSITION') },
  { id: 'created_at', label: t('PIPELINE.SORT_OPTIONS.CREATED_AT') },
  { id: 'title', label: t('PIPELINE.SORT_LABELS.TITLE') },
  { id: 'priority', label: t('PIPELINE.SORT_LABELS.PRIORITY') },
  { id: 'value', label: t('PIPELINE.SORT_LABELS.VALUE') },
  { id: 'next_activity', label: t('PIPELINE.SORT_OPTIONS.NEXT_ACTIVITY') },
  { id: 'last_activity', label: t('PIPELINE.SORT_OPTIONS.LAST_ACTIVITY') },
  {
    id: 'conversation_activity',
    label: t('PIPELINE.SORT_OPTIONS.CONVERSATION_ACTIVITY'),
  },
]);

// Storage keys with user-scoped prefix to isolate data between users
const SETTINGS_STORAGE_KEY = computed(
  () => `${userScopedKeyPrefix.value}pipeline_column_${props.id}_settings`
);
const SORT_STORAGE_KEY = computed(
  () => `${userScopedKeyPrefix.value}pipeline_column_${props.id}_sort`
);
const APPLIED_FILTER_STORAGE_KEY = computed(
  () =>
    `${userScopedKeyPrefix.value}pipeline_column_${props.id}_applied_filters`
);
// COLLAPSED_ITEMS_STORAGE_KEY removida — o colapso em massa passa pelo
// setter em lote do PipelineView (dono do localStorage user-scoped).
const SCROLL_STATE_STORAGE_KEY = computed(
  () => `${userScopedKeyPrefix.value}pipeline_column_${props.id}_scroll_state`
);

// Holds scroll state to restore on first items load (used after page refresh)
let scrollStateToRestore = null;

// Fechar menu ao clicar fora
const closeOptionsMenuOnClickOutside = () => {
  if (showOptionsMenu.value) {
    showOptionsMenu.value = false;
  }
};

// Named handler for emitter to properly remove on unmount
const handleCloseAllDropdowns = () => {
  showOptionsMenu.value = false;
};

// Salvar configurações no localStorage (user-scoped)
const saveColumnSettings = () => {
  localStorage.setItem(
    SETTINGS_STORAGE_KEY.value,
    JSON.stringify(columnSettings.value)
  );
  emit('hideToggle', { id: props.id, hidden: columnSettings.value.hideColumn });
};

// Salvar configurações de ordenação (user-scoped)
const saveSortSettings = () => {
  localStorage.setItem(
    SORT_STORAGE_KEY.value,
    JSON.stringify(columnSort.value)
  );
};

const filteredItems = computed(() => {
  if (columnSettings.value.hideColumn) {
    return [];
  }

  // 1. Start with the items passed from the parent (already filtered by global search)
  let items = [...props.items];

  // 2. Apply local column filters (priority, value, agent, date).
  // Matching extraído para helpers puros (columnFilterMatchers.js) — corrige
  // a leitura de priority/owner_id na raiz do payload e o valor com
  // semântica server-side (expected_revenue → item_details.value).
  if (columnFilters.value) {
    items = items.filter(item =>
      matchesColumnFilters(item, columnFilters.value)
    );
  }

  // 3. Apply status filters (won/lost) using database fields instead of stale item_details.status
  items = items.filter(item => {
    // Use won_at/lost_at from database instead of item_details.status (which is stale)
    const isWon = Boolean(item.won_at);
    const isLost = Boolean(item.lost_at);
    // Keep items that are not 'won' or 'lost' (active/open items)
    if (!isWon && !isLost) return true;
    // Apply toggles only to 'won' and 'lost' items
    if (isWon && !columnSettings.value.showWon) return false;
    if (isLost && !columnSettings.value.showLost) return false;
    return true; // Item passes status filters
  });
  // 4. Apply sorting
  return items.sort((a, b) => {
    const direction = columnSort.value.direction === 'asc' ? 1 : -1;
    const type = columnSort.value.type;

    let valueA;
    let valueB;

    switch (type) {
      case 'position': {
        // Ordem manual do kanban. position é NOT NULL; usa id como
        // desempate para uma ordenação estável quando posições empatam.
        valueA = a.position ?? 0;
        valueB = b.position ?? 0;
        if (valueA !== valueB) return direction * (valueA - valueB);
        return direction * ((a.id || 0) - (b.id || 0));
      }

      case 'title':
        // item_details.title é mirror JSONB que pode faltar; fallback para o
        // title raiz (coluna do DB) — mesma cadeia do displayTitle.
        valueA = a.item_details?.title || a.title || '';
        valueB = b.item_details?.title || b.title || '';
        return direction * valueA.localeCompare(valueB);

      case 'priority': {
        const priorityOrder = {
          urgent: 4,
          high: 3,
          medium: 2,
          low: 1,
          none: 0, // Ensure 'none' or undefined priority is handled
        };
        // Ciclo 6: priority vive na RAIZ do payload (getCardPriority com
        // fallback legacy em item_details) — antes lia só item_details e
        // ordenava tudo como 'none'.
        valueA = priorityOrder[getCardPriority(a)] ?? 0;
        valueB = priorityOrder[getCardPriority(b)] ?? 0;
        return direction * (valueA - valueB);
      }

      case 'value':
        // Ciclo 6: mesma semântica server-side dos filtros (expected_revenue
        // → item_details.value); cards sem valor ordenam como 0 (comportamento
        // anterior preservado).
        valueA = getCardValue(a) ?? 0;
        valueB = getCardValue(b) ?? 0;
        return direction * (valueA - valueB);

      case 'next_activity':
        // Cards without next activity sort to end (asc) or beginning (desc)
        valueA = a.next_activity_at
          ? new Date(a.next_activity_at)
          : new Date(9999, 0);
        valueB = b.next_activity_at
          ? new Date(b.next_activity_at)
          : new Date(9999, 0);
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);

      case 'last_activity':
        // Cards without pipeline activity sort to end (desc) or beginning (asc)
        valueA = a.last_pipeline_activity_at
          ? new Date(a.last_pipeline_activity_at)
          : new Date(0);
        valueB = b.last_pipeline_activity_at
          ? new Date(b.last_pipeline_activity_at)
          : new Date(0);
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);

      case 'conversation_activity':
        // Cards without linked conversation sort to end
        valueA = a.conversation_last_activity_at
          ? new Date(a.conversation_last_activity_at)
          : new Date(0);
        valueB = b.conversation_last_activity_at
          ? new Date(b.conversation_last_activity_at)
          : new Date(0);
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);

      case 'created_at': // Default sorting
      default:
        valueA = new Date(a.created_at);
        valueB = new Date(b.created_at);
        // Handle potential invalid dates during sorting
        if (Number.isNaN(valueA.getTime())) return direction;
        if (Number.isNaN(valueB.getTime())) return -direction;
        return direction * (valueA - valueB);
    }
  });
});
// Lista de itens a serem realmente exibidos com lazy loading
const displayedItems = computed(() => {
  return filteredItems.value.slice(0, itemsToShowCount.value);
});
// Estado de colapso em massa DERIVADO do ref compartilhado — o ref local
// antigo (sempre false no mount) dessincronizava do estado real após
// remount/toggles individuais.
const allItemsCollapsed = computed(
  () =>
    filteredItems.value.length > 0 &&
    filteredItems.value.every(item => !!collapsedItemsShared.value[item.id])
);

// Resultados do filtro para exibir no modal
const filteredResults = computed(() => {
  if (!columnFilters.value) return { total: 0, stages: {} };

  const stages = {};
  stages[props.title] = filteredItems.value.length;

  return {
    total: filteredItems.value.length,
    stages,
  };
});
// Ciclo 6 (2026-06-10): total da coluna com a MESMA semântica de valor dos
// filtros/sort (getCardValue: expected_revenue → item_details.value). Antes
// lia só item_details.value e cards com valor em expected_revenue não
// entravam no total do header (achado QA do ciclo 5).
const columnTotal = computed(() => {
  return filteredItems.value.reduce((total, item) => {
    return total + (getCardValue(item) ?? 0);
  }, 0);
});
const DEFAULT_COLOR = '#64748b';

const isValidHex = color => color && /^#[0-9a-fA-F]{6}$/.test(color);

const safeColor = computed(() =>
  isValidHex(props.color) ? props.color : DEFAULT_COLOR
);

const darkenColor = color => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkenAmount = 0.85;
  const dr = Math.floor(r * darkenAmount);
  const dg = Math.floor(g * darkenAmount);
  const db = Math.floor(b * darkenAmount);

  return `#${dr.toString(16).padStart(2, '0')}${dg.toString(16).padStart(2, '0')}${db.toString(16).padStart(2, '0')}`;
};

const textColor = computed(() => darkenColor(safeColor.value));

// NOTE: backdropFilter removido aqui — `backdrop-filter` cria stacking
// context novo (CSS spec) e isso prendia dropdowns absolute dos cards
// dentro da coluna, fazendo o card seguinte se sobrepor a eles.
// Mantemos a estética glassy via cor + borda + sombra coloridas.
const columnStyle = computed(() => ({
  backgroundColor: `${safeColor.value}10`,
  borderTop: `4px solid ${safeColor.value}`,
  border: `1px solid ${safeColor.value}20`,
  boxShadow: `0 8px 32px -8px ${safeColor.value}30`,
}));

const countStyle = computed(() => ({
  backgroundColor: `${safeColor.value}15`,
  color: textColor.value,
}));

// Deal status modals (won/lost) hosted at the column level so the inline board
// dropdown captura o valor/motivo — alguns funis exigem won_value, então NÃO dá
// pra marcar ganho direto pela API sem input (gerava 422 silencioso/"nada
// acontece"). Reopen não precisa de input → chamada direta.
const showWonModal = ref(false);
const showLostModal = ref(false);
const dealStatusCard = ref(null);

// Aplica o payload PARCIAL de deal_status (id, status, pipeline_stage, won_*/lost_*)
// sobre o card existente no store, sem perder os demais campos, e emite o evento
// de troca de etapa quando o card auto-move para Ganho/Perdido.
const applyDealStatusResult = (card, payload) => {
  if (!payload) return;
  const updatedCard = { ...card, ...payload };
  store.commit('pipelineCards/REPLACE_CARD', updatedCard);
  const oldStage = card.pipeline_stage || props.id;
  if (updatedCard.pipeline_stage && updatedCard.pipeline_stage !== oldStage) {
    // Ganho/Perdido/Reopen movem o card de etapa (→ Ganho/Perdido ou de volta),
    // mas não passam pelo move otimista do drag — sem este ajuste o badge das
    // colunas origem/destino fica defasado até o próximo fetch (QA 2026-06-12).
    store.commit('pipelineCards/ADJUST_STAGE_COUNTS', {
      fromStage: oldStage,
      toStage: updatedCard.pipeline_stage,
    });
    emit('cardStageChanged', {
      card: updatedCard,
      oldStage,
      newStage: updatedCard.pipeline_stage,
    });
  } else {
    emit('itemsUpdated');
  }
};

const handleStatusChange = async ({ card, status }) => {
  // Won/Lost abrem o modal dedicado (captura won_value/motivo via endpoint de
  // deal_status). Reopen vai direto.
  if (status === 'won') {
    dealStatusCard.value = card;
    showWonModal.value = true;
    return;
  }
  if (status === 'lost') {
    dealStatusCard.value = card;
    showLostModal.value = true;
    return;
  }
  try {
    const DealStatusAPI = (await import('dashboard/api/pipeline/dealStatus'))
      .default;
    const response = await DealStatusAPI.reopen(card.id);
    applyDealStatusResult(card, response.data || response);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STATUS_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message:
        error.response?.data?.error ||
        t('PIPELINE.MESSAGES.STATUS_UPDATED_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Sucesso de um dos modais de deal_status: aplica o payload e fecha.
const handleDealStatusSuccess = payload => {
  if (dealStatusCard.value)
    applyDealStatusResult(dealStatusCard.value, payload);
  showWonModal.value = false;
  showLostModal.value = false;
  dealStatusCard.value = null;
};

const handleDrop = event => {
  isDragOver.value = false;
  const itemID = event.dataTransfer.getData('text/plain');
  if (itemID) {
    const numericId = parseInt(itemID, 10);
    // Garantir que o ID seja passado como número para o componente pai
    emit('drop', {
      itemId: numericId,
      columnId: props.id,
    });
  }
};

const handleDragOver = event => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const handleDragEnter = event => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = event => {
  // Only set to false if leaving the column entirely, not just entering a child
  const relatedTarget = event.relatedTarget;
  if (!event.currentTarget.contains(relatedTarget)) {
    isDragOver.value = false;
  }
};

// Column width calculation using design tokens
// Design system: w-72 (288px) - consistent fixed width for all pipelines
// Note: Actual width is controlled by parent .pipeline-column-wrapper in PipelineTab.vue
const columnWidth = computed(() => {
  // Fixed width for consistent look across all pipelines
  return {
    minWidth: '288px',
    maxWidth: '288px',
    width: '288px',
  };
});

const hasActiveFilters = computed(() => {
  // Check local column filters (priority, value, agent, date).
  // isSet em vez de truthiness: min/max = 0 é filtro válido — sem isso o
  // emptyStateMessage cai em "Carregando itens..." com filtro-zero ativo.
  const hasColumnFilters =
    columnFilters.value &&
    (columnFilters.value.priority.length > 0 ||
      isSet(columnFilters.value.value.min) ||
      isSet(columnFilters.value.value.max) ||
      columnFilters.value.agent_id ||
      columnFilters.value.date.start ||
      columnFilters.value.date.end);

  // Check status toggles (won/lost hidden counts as a filter)
  const hasStatusFilters =
    !columnSettings.value.showWon || !columnSettings.value.showLost;

  return hasColumnFilters || hasStatusFilters;
});

// Total count from backend (all items, not just loaded).
// Usado no badge da coluna, no auto-load e no fallback de "tem mais?".
// meta.stage_counts já vem filtrado server-side pelos filtros do board.
const backendStageCount = computed(() => {
  const meta = store.state.pipelineCards.meta;
  if (meta?.stage_counts && meta.stage_counts[props.id] !== undefined) {
    return meta.stage_counts[props.id];
  }
  return null;
});

// Ciclo 3b — metadado de paginação server-side desta coluna (cursor keyset).
// Populado por fetchBoardByStages/fetchStageCards; null quando o flat foi
// carregado pelo fetch global legado (list/agenda → board).
const stagePagination = computed(
  () => store.getters['pipelineCards/getCardsByStage'][props.id] || null
);

const isStageLoading = computed(() => stagePagination.value?.loading || false);

// Tem mais cards no backend? Com estado de stage usa o has_more da última
// página; sem estado (board alimentado pelo fetch global), compara o total
// do backend com o que está carregado localmente.
const stageHasMore = computed(() => {
  if (stagePagination.value) return stagePagination.value.hasMore;
  return (
    backendStageCount.value !== null &&
    props.items.length < backendStageCount.value
  );
});

// Ciclo 9 — total monetário HONESTO sob paginação por coluna. columnTotal
// soma getCardValue só dos cards CARREGADOS (filteredItems ⊂ janela do cursor
// keyset), e o backend NÃO expõe agregado de VALOR por stage no meta (só
// stage_counts). Um drag muda a composição da janela carregada: o card movido
// entra no flat do destino na hora (UPDATE_ITEM_STAGE), mas no reload ele cai
// fora da primeira página do stage (position = max+1 ancora no FIM), então o
// número divergia entre sessão e reload (QA ciclo 8: R$ 1.281.564 vs
// 1.254.941). Enquanto houver mais cards no servidor o total é parcial por
// natureza — sinalizamos com sufixo "+" e tooltip explícito em vez de fingir
// um total exato que nenhuma das duas janelas representa.
const isColumnTotalPartial = computed(() => stageHasMore.value);

const formattedTotal = computed(() => {
  if (!columnTotal.value) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(columnTotal.value);
});

const displayedTotal = computed(() => {
  if (!formattedTotal.value) return '';
  return isColumnTotalPartial.value
    ? `${formattedTotal.value}+`
    : formattedTotal.value;
});

// Tooltip do total: count usa filteredItems (o conjunto realmente somado —
// antes usava items.length, ignorando filtros locais e won/lost ocultos).
const columnTotalTooltip = computed(() => {
  const params = {
    count: filteredItems.value.length,
    total: formattedTotal.value,
  };
  return isColumnTotalPartial.value
    ? t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_PARTIAL_TOOLTIP', params)
    : t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_TOOLTIP', params);
});

// Salvar posição de scroll e paginação local (user-scoped)
// Não salva quando filtros estão ativos para evitar restauração inconsistente
const saveScrollState = () => {
  if (hasActiveFilters.value || !scrollContainer.value) return;
  localStorage.setItem(
    SCROLL_STATE_STORAGE_KEY.value,
    JSON.stringify({
      itemsToShowCount: itemsToShowCount.value,
      scrollTop: scrollContainer.value.scrollTop,
    })
  );
};

// Função para carregar mais itens (lazy loading local)
const loadMoreItems = () => {
  if (itemsToShowCount.value < filteredItems.value.length) {
    itemsToShowCount.value = Math.min(
      itemsToShowCount.value + itemsIncrement,
      filteredItems.value.length
    );
    saveScrollState();
  }
};

// Ciclo 3b — "carregar mais" da coluna usa paginação server-side POR STAGE
// (cursor keyset via fetchStageCards), substituindo o append global por
// offset que misturava stages e tinha teto silencioso em 500 cards. O fetch
// por stage não aciona o skeleton (loading global), então o scroll do
// usuário é preservado; o merge no flat dedupa por id.
const loadMoreFromAPI = async () => {
  const pipelineId = store.getters['pipelines/getSelectedPipeline']?.id;
  if (!pipelineId || !stageHasMore.value) return;
  if (isStageLoading.value || store.state.pipelineCards.loading) return;

  await store.dispatch('pipelineCards/fetchStageCards', {
    pipelineId,
    stageId: props.id,
    append: true,
  });
};

// Handler do evento de scroll para lazy loading
const handleScroll = () => {
  if (!scrollContainer.value) return;

  updateScrollIndicator();
  saveScrollState();

  const { scrollTop, scrollHeight, clientHeight } = scrollContainer.value;
  const threshold = 150; // Carregar quando faltar 150px para o fim

  if (scrollTop + clientHeight >= scrollHeight - threshold) {
    if (itemsToShowCount.value < filteredItems.value.length) {
      // Still have local items to show
      loadMoreItems();
    } else {
      // All local items shown, try loading more from backend
      loadMoreFromAPI();
    }
  }
};

// Funções para o filtro de coluna
const handleFilter = () => {
  showFilterModal.value = true;
};

const handleFilterApply = appliedFilters => {
  // Check if filters are effectively empty/cleared
  const isCleared =
    !appliedFilters ||
    ((!appliedFilters.priority || appliedFilters.priority.length === 0) &&
      (!appliedFilters.value ||
        (appliedFilters.value.min === null &&
          appliedFilters.value.max === null)) &&
      !appliedFilters.agent_id &&
      (!appliedFilters.date ||
        (!appliedFilters.date.start && !appliedFilters.date.end)));

  if (isCleared) {
    columnFilters.value = null;
    localStorage.removeItem(APPLIED_FILTER_STORAGE_KEY.value);
  } else {
    columnFilters.value = appliedFilters;
    localStorage.setItem(
      APPLIED_FILTER_STORAGE_KEY.value,
      JSON.stringify(appliedFilters)
    );
  }
  // Clear saved scroll state — filter change resets paginação
  localStorage.removeItem(SCROLL_STATE_STORAGE_KEY.value);
  scrollStateToRestore = null;
  showFilterModal.value = false;
  // Reset lazy load count on filter change
  itemsToShowCount.value = 6;
  nextTick(() => {
    handleScroll();
  }); // Check if scroll needs adjustment
};

// Ciclo 3b — botão "carregar mais": soma o restante local (reveal por slice)
// com o restante no servidor (total do backend - carregado), para o label
// refletir o que realmente falta, não só a página em memória.
const remainingCount = computed(() => {
  const localRemaining = filteredItems.value.length - itemsToShowCount.value;
  const remoteRemaining =
    backendStageCount.value !== null
      ? backendStageCount.value - filteredItems.value.length
      : 0;
  return Math.max(localRemaining, 0) + Math.max(remoteRemaining, 0);
});

const showLoadMoreButton = computed(
  () =>
    itemsToShowCount.value < filteredItems.value.length || stageHasMore.value
);

const loadMoreLabel = computed(() => {
  if (isStageLoading.value) return t('PIPELINE.LOADING_ITEMS');
  if (remainingCount.value > 0) {
    return t('PIPELINE.LOAD_MORE', { remaining: remainingCount.value });
  }
  return t('PIPELINE.LOAD_MORE_SIMPLE');
});

// Clique no botão: primeiro esgota o reveal local (slice), depois pagina o
// stage no servidor (cursor).
const handleLoadMoreClick = () => {
  if (itemsToShowCount.value < filteredItems.value.length) {
    loadMoreItems();
  } else {
    loadMoreFromAPI();
  }
};

// Display count: prefere backend total quando não há filtros locais ativos.
// Audit 2026-04-25: cliente reportou que o badge mostrava só os carregados
// (50/500), enquanto o stage real tinha 2442 cards. Agora mostra o total
// real, e o filtro local fica refletido em filteredItems.length quando
// houver filtros aplicados.
const displayCount = computed(() => {
  if (!hasActiveFilters.value && backendStageCount.value !== null) {
    return backendStageCount.value;
  }
  return filteredItems.value.length;
});

// Tooltip detalhado mostrando carregados vs total para transparência.
const displayCountTooltip = computed(() => {
  const loaded = filteredItems.value.length;
  const total = backendStageCount.value;
  if (total === null || total === loaded) return null;
  return t('PIPELINE.COLUMN_OPTIONS.LOADED_OF_TOTAL', {
    loaded,
    total,
    defaultValue: `${loaded} carregados de ${total}`,
  });
});

// Empty-state message. Bugfix 2026-06-10: com filtros LOCAIS ativos e 0
// matches, a coluna ficava presa em "Carregando itens..." (o ternário antigo
// checava backendStageCount > 0 antes de hasActiveFilters, e stage_counts é
// cego aos filtros locais). Agora: carregando de verdade → LOADING; filtros
// ativos sem match → NO_FILTERED_ITEMS (com o botão "carregar mais" como
// escape manual para buscar matches nas próximas páginas do stage).
const emptyStateMessage = computed(() => {
  if (isStageLoading.value || store.state.pipelineCards.loading) {
    return t('PIPELINE.LOADING_ITEMS');
  }
  if (hasActiveFilters.value) return t('PIPELINE.NO_FILTERED_ITEMS');
  if (backendStageCount.value > 0) return t('PIPELINE.LOADING_ITEMS');
  return t('PIPELINE.NO_ITEMS');
});

// Auto-fetch when backend reports items exist but none are loaded locally.
// This covers edge cases where pagination/cache causes empty columns despite
// stage_counts showing items (the "Followup 4 → Nenhum Item" bug).
watch(
  () => [props.items.length, backendStageCount.value],
  ([localCount, backendCount]) => {
    if (
      localCount === 0 &&
      backendCount > 0 &&
      !store.state.pipelineCards.loading
    ) {
      loadMoreFromAPI();
    }
  }
);

// Resetar a contagem de itens visíveis quando item count changes.
// Na primeira carga após refresh, restaura o estado salvo em vez de resetar para 6.
// Mudanças subsequentes (real-time updates) não resetam a paginação.
watch(
  () => filteredItems.value.length,
  (newLength, oldLength) => {
    if (scrollStateToRestore && newLength > 0) {
      // Restaurar estado salvo (page refresh com scroll/paginação preservados)
      itemsToShowCount.value = Math.min(
        scrollStateToRestore.itemsToShowCount || 6,
        newLength
      );
      const savedScrollTop = scrollStateToRestore.scrollTop;
      scrollStateToRestore = null; // Restaurar apenas uma vez
      nextTick(() => {
        if (scrollContainer.value) {
          if (savedScrollTop) {
            scrollContainer.value.scrollTop = savedScrollTop;
          }
          updateScrollIndicator();
          if (
            scrollContainer.value.scrollHeight <=
            scrollContainer.value.clientHeight
          ) {
            loadMoreItems();
          }
        }
      });
    } else if (oldLength === 0 && newLength > 0) {
      // Primeira carga de itens: resetar para 6
      itemsToShowCount.value = 6;
      nextTick(() => {
        if (scrollContainer.value) {
          if (
            scrollContainer.value.scrollHeight <=
            scrollContainer.value.clientHeight
          ) {
            loadMoreItems();
          }
        }
      });
    }
    // Mudanças subsequentes (real-time add/remove) não resetam itemsToShowCount
  }
);

// Carregar configurações do localStorage (user-scoped)
onMounted(() => {
  // Load non-filter settings
  const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY.value);
  if (savedSettings) {
    columnSettings.value = JSON.parse(savedSettings);
  }
  const savedSort = localStorage.getItem(SORT_STORAGE_KEY.value);
  if (savedSort) {
    columnSort.value = JSON.parse(savedSort);
  }
  // Load filters from user-scoped storage key
  const savedFilters = localStorage.getItem(APPLIED_FILTER_STORAGE_KEY.value);
  if (savedFilters) {
    try {
      const parsed = JSON.parse(savedFilters);
      if (parsed && parsed.agent_id !== null && parsed.agent_id !== undefined) {
        parsed.agent_id = Number(parsed.agent_id);
      }
      if (typeof parsed === 'object' && parsed !== null) {
        columnFilters.value = parsed;
      }
    } catch (e) {
      // Error handled silently
    }
  }

  // Restaurar estado de scroll/paginação se não houver filtros ativos
  if (!columnFilters.value) {
    const savedScrollState = localStorage.getItem(
      SCROLL_STATE_STORAGE_KEY.value
    );
    if (savedScrollState) {
      try {
        const parsed = JSON.parse(savedScrollState);
        if (parsed?.itemsToShowCount) {
          if (filteredItems.value.length > 0) {
            // Itens já disponíveis: restaurar imediatamente
            itemsToShowCount.value = Math.min(
              parsed.itemsToShowCount,
              filteredItems.value.length
            );
            nextTick(() => {
              if (scrollContainer.value && parsed.scrollTop) {
                scrollContainer.value.scrollTop = parsed.scrollTop;
                updateScrollIndicator();
              }
            });
          } else {
            // Itens ainda carregando: o watcher vai restaurar quando chegarem
            scrollStateToRestore = parsed;
          }
        }
      } catch (e) {
        // Error handled silently
      }
    }
  }

  // Adicionar listener de scroll para lazy loading
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll);
    // Verificar se precisa carregar mais itens logo no início
    nextTick(() => {
      if (
        scrollContainer.value &&
        scrollContainer.value.scrollHeight <= scrollContainer.value.clientHeight
      ) {
        loadMoreItems();
      }
      updateScrollIndicator();
    });
  }

  // Fechar menu ao clicar fora
  document.addEventListener('click', closeOptionsMenuOnClickOutside);

  // Listen for global dropdown close event
  emitter.on('closeAllPipelineDropdowns', handleCloseAllDropdowns);
});
onUnmounted(() => {
  document.removeEventListener('click', closeOptionsMenuOnClickOutside);
  emitter.off('closeAllPipelineDropdowns', handleCloseAllDropdowns);

  // Remover listener de scroll
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll);
  }
});
// Atualizar o watch para sincronizar as opções
watch(
  () => columnSettings.value.showAll,
  newValue => {
    if (newValue) {
      columnSettings.value.showWon = true;
      columnSettings.value.showLost = true;
    }
    saveColumnSettings();
  }
);

// Atualizar o watch para a opção "todos"
watch(
  [() => columnSettings.value.showWon, () => columnSettings.value.showLost],
  ([showWon, showLost]) => {
    columnSettings.value.showAll = showWon && showLost;
  }
);

const toggleOptionsMenu = () => {
  const wasOpen = showOptionsMenu.value;
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showOptionsMenu.value = !wasOpen;
};

const handleEditStage = () => {
  editingStageForm.value = {
    name: props.title,
    color: props.color,
    description: props.description || '',
  };
  showEditStageModal.value = true;
  showOptionsMenu.value = false;
};

const handleSaveStage = async () => {
  try {
    const pipeline = store.getters['pipelines/getSelectedPipeline'];
    const updatedStages = { ...pipeline.stages };

    updatedStages[props.id] = {
      ...updatedStages[props.id],
      name: editingStageForm.value.name,
      color: editingStageForm.value.color,
      description: editingStageForm.value.description,
    };

    await PipelinesAPI.update(pipeline.id, {
      stages: updatedStages,
    });
    showEditStageModal.value = false;

    // Força atualização do store
    await store.dispatch('pipelines/fetch', { force: true });

    // Emite evento para atualizar o pipeline
    emit('itemsUpdated');

    // Notifica sucesso
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STAGE_UPDATED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.STAGE_UPDATED_ERROR'),
      action: { type: 'error' },
    });
  }
};

// Recolher/Expandir todos os itens da coluna.
// Bug (report do usuário 2026-06-10): a versão antiga escrevia DIRETO no
// localStorage e emitia um evento que ninguém escuta — desde o refactor
// Card-Q1.2 os cards leem o ref reativo `collapsedItems` injetado pelo
// PipelineView, então nada re-renderizava. Agora passa pelo setter em lote
// provido pela view (1 write reativo + localStorage para N cards).
const toggleAllItems = () => {
  const target = !allItemsCollapsed.value;
  const entries = {};
  filteredItems.value.forEach(item => {
    entries[item.id] = target;
  });
  updateCollapsedItems(entries);

  // Fecha o menu de opções
  showOptionsMenu.value = false;
};
</script>

<template>
  <section
    role="region"
    :aria-label="
      t('PIPELINE.STAGE.LABEL') +
      ': ' +
      title +
      ', ' +
      displayCount +
      ' ' +
      (displayCount === 1 ? t('PIPELINE.CARD_SINGULAR') : t('PIPELINE.CARDS'))
    "
    class="flex flex-col h-full rounded-2xl pipeline-column"
    :style="{ ...columnWidth, ...columnStyle }"
  >
    <header
      class="pl-4 pr-4 pt-3 pb-3 rounded-t-2xl"
      :style="{ borderBottom: `1px solid ${safeColor}59` }"
    >
      <!-- Layout (report do usuário 2026-06-10): título trunca (min-w-0 +
           truncate) e o bloco de ações tem shrink-0 — antes, título longo +
           total R$ espremiam o botão ⋮ contra a borda da coluna. -->
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <h2
            :id="`column-title-${id}`"
            class="text-sm font-medium truncate"
            :style="{ color: textColor }"
            :title="title"
          >
            {{ title }}
          </h2>
          <span
            v-tooltip="
              displayCountTooltip
                ? {
                    content: displayCountTooltip,
                    delay: { show: 300, hide: 0 },
                    placement: 'top',
                  }
                : null
            "
            class="inline-flex items-center justify-center min-w-5 h-5 px-1 text-xs font-medium rounded-full shrink-0"
            :class="{ 'cursor-help': displayCountTooltip }"
            :style="countStyle"
          >
            {{ displayCount }}
          </span>
          <!-- Indicador de filtro ativo -->
          <span
            v-if="hasActiveFilters"
            class="w-1.5 h-1.5 rounded-full inline-block shrink-0"
            :style="{ backgroundColor: textColor }"
          />
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <span
            v-if="formattedTotal"
            v-tooltip="{
              content: columnTotalTooltip,
              delay: { show: 300, hide: 0 },
              placement: 'top',
            }"
            class="text-xs font-medium cursor-help whitespace-nowrap"
            :style="{ color: textColor }"
          >
            {{ displayedTotal }}
          </span>
          <!-- Botão de filtro -->
          <button
            :aria-label="t('PIPELINE.FILTER_ITEMS')"
            class="p-1 text-n-slate-11 hover:text-n-slate-12"
            @click="handleFilter"
          >
            <fluent-icon icon="filter" size="16" />
          </button>
          <button
            :aria-label="t('PIPELINE.ADD_CARD')"
            data-tour="add-card-button"
            class="p-1.5 rounded-md text-woot-500 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/40 transition-all"
            @click="$emit('add', id)"
          >
            <fluent-icon icon="add" size="16" />
          </button>
          <!-- Botão de mais opções -->
          <div class="relative">
            <button
              :aria-label="t('PIPELINE.COLUMN_OPTIONS.TITLE') || 'Options'"
              :aria-expanded="showOptionsMenu"
              aria-haspopup="true"
              class="p-1 text-n-slate-11 hover:text-n-slate-12 options-menu-button"
              @click.stop="toggleOptionsMenu"
            >
              <fluent-icon icon="more-vertical" size="16" />
            </button>

            <!-- Menu de Opções -->
            <div
              v-if="showOptionsMenu"
              class="absolute right-0 top-full mt-1 bg-n-solid-3 rounded-xl shadow-lg outline outline-1 outline-n-weak py-2 w-48 z-10 options-menu-content"
              @click.stop
            >
              <div class="px-4 py-2">
                <button
                  class="flex items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand"
                  @click="handleEditStage"
                >
                  <fluent-icon icon="edit" size="16" />
                  <span>{{ t('PIPELINE.COLUMN_OPTIONS.EDIT_STAGE') }}</span>
                </button>
              </div>
              <div class="border-t border-n-weak my-1" />
              <div class="px-4 py-2">
                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input
                    :id="fieldId('show-all')"
                    v-model="columnSettings.showAll"
                    name="show_all"
                    type="checkbox"
                    class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                    @change="saveColumnSettings"
                  />
                  <span>{{ t('PIPELINE.COLUMN_OPTIONS.SHOW_ALL') }}</span>
                </label>
              </div>
              <!-- Só mostra opções de ganho/perdido se habilitado -->
              <template v-if="enableWonLostStages">
                <div class="px-4 py-2">
                  <label
                    class="flex items-center gap-2 text-sm text-n-slate-11"
                  >
                    <input
                      :id="fieldId('show-won')"
                      v-model="columnSettings.showWon"
                      name="show_won"
                      type="checkbox"
                      class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                      @change="saveColumnSettings"
                    />
                    <span>{{ t('PIPELINE.COLUMN_OPTIONS.SHOW_WON') }}</span>
                  </label>
                </div>
                <div class="px-4 py-2">
                  <label
                    class="flex items-center gap-2 text-sm text-n-slate-11"
                  >
                    <input
                      :id="fieldId('show-lost')"
                      v-model="columnSettings.showLost"
                      name="show_lost"
                      type="checkbox"
                      class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                      @change="saveColumnSettings"
                    />
                    <span>{{ t('PIPELINE.COLUMN_OPTIONS.SHOW_LOST') }}</span>
                  </label>
                </div>
              </template>
              <div class="px-4 py-2">
                <label class="flex items-center gap-2 text-sm text-n-slate-11">
                  <input
                    :id="fieldId('hide-column')"
                    v-model="columnSettings.hideColumn"
                    name="hide_column"
                    type="checkbox"
                    class="rounded border-n-weak text-woot-500 shadow-sm focus:border-woot-500 focus:ring focus:ring-woot-500 focus:ring-opacity-50"
                    @change="saveColumnSettings"
                  />
                  <span>{{ t('PIPELINE.COLUMN_OPTIONS.HIDE_STAGE') }}</span>
                </label>
              </div>
              <div class="px-4 py-2">
                <button
                  class="flex items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand"
                  @click="showSortModal = true"
                >
                  <fluent-icon icon="arrow-sort" size="16" />
                  <span>{{ t('PIPELINE.COLUMN_OPTIONS.SORT') }}</span>
                </button>
              </div>
              <div class="border-t border-n-weak my-1" />
              <div class="px-4 py-2">
                <button
                  class="flex items-center gap-2 text-sm text-n-slate-11 w-full hover:text-n-brand"
                  @click="toggleAllItems"
                >
                  <fluent-icon
                    :icon="
                      allItemsCollapsed ? 'arrow-expand' : 'arrow-outwards'
                    "
                    size="16"
                  />
                  <span>{{
                    allItemsCollapsed
                      ? t('PIPELINE.COLUMN_OPTIONS.EXPAND_ALL')
                      : t('PIPELINE.COLUMN_OPTIONS.COLLAPSE_ALL')
                  }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <div
      ref="scrollContainer"
      class="flex-1 overflow-y-auto rounded-b-2xl transition-colors duration-200 motion-reduce:transition-none m-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      :class="
        isDragOver
          ? 'border-2 border-dashed border-n-brand bg-n-alpha-1'
          : 'border-2 border-transparent'
      "
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
    >
      <!-- Drop indicator -->
      <div
        v-if="isDragOver"
        class="h-0.5 bg-n-brand rounded mx-3 mt-2 animate-pulse motion-reduce:animate-none"
      />
      <div class="px-4">
        <div
          role="list"
          :aria-labelledby="`column-title-${id}`"
          class="py-2 space-y-2.5"
        >
          <template v-if="displayedItems.length">
            <PipelineCard
              v-for="(item, index) in displayedItems"
              :key="item.id"
              :card="item"
              :stage-name="title"
              :aria-posinset="index + 1"
              :aria-setsize="displayedItems.length"
              draggable
              @click="modifiedItem => $emit('itemClick', modifiedItem)"
              @open-details="modifiedItem => $emit('itemClick', modifiedItem)"
              @edit="modifiedItem => $emit('edit', modifiedItem)"
              @delete="$emit('delete', item)"
              @status-change="handleStatusChange"
            />
            <!-- Load more indicator (local slice reveal + server-side cursor) -->
            <button
              v-if="showLoadMoreButton"
              :disabled="isStageLoading"
              class="w-full py-2 text-xs font-medium text-n-slate-11 hover:text-n-brand bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
              @click="handleLoadMoreClick"
            >
              {{ loadMoreLabel }}
            </button>
          </template>
          <template v-else>
            <div
              role="status"
              aria-live="polite"
              class="flex items-center justify-center h-24 text-body-main text-n-slate-10"
            >
              {{ emptyStateMessage }}
            </div>
            <!-- Coluna vazia localmente mas com cards no servidor (fallback de
                 cache global, falha parcial de stage): oferece o fetch da
                 primeira página em vez de um beco sem saída (achado Codex). -->
            <button
              v-if="showLoadMoreButton"
              :disabled="isStageLoading"
              class="w-full py-2 text-xs font-medium text-n-slate-11 hover:text-n-brand bg-n-alpha-2 hover:bg-n-alpha-3 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
              @click="handleLoadMoreClick"
            >
              {{ loadMoreLabel }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <!-- Scroll indicator gradient -->
    <div
      v-if="canScrollDown"
      class="h-6 pointer-events-none rounded-b-xl -mt-6 relative z-10 bg-gradient-to-t from-n-solid-2/80 to-transparent"
    />

    <!-- Modal de filtro (montagem lazy: o componente despacha agents/labels
         no onMounted; eager em cada coluna = N fetches no load do board) -->
    <PipelineFilter
      v-if="showFilterModal"
      :show="showFilterModal"
      :filtered-results="filteredResults"
      :current-pipeline="store.getters['pipelines/getSelectedPipeline']"
      :initial-filters="columnFilters"
      scope="column"
      :column-name="title"
      size="large"
      @close="showFilterModal = false"
      @apply="handleFilterApply"
    />

    <!-- Modal de Edição de Etapa -->
    <Modal
      v-model:show="showEditStageModal"
      @close="() => (showEditStageModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.EDIT_STAGE_MODAL.TITLE') }}
        </h3>

        <div class="space-y-4">
          <div>
            <label
              :for="fieldId('stage-name')"
              class="block text-sm font-medium mb-1"
              >{{ t('PIPELINE.EDIT_STAGE_MODAL.NAME') }}</label
            >
            <input
              :id="fieldId('stage-name')"
              v-model="editingStageForm.name"
              name="stage_name"
              type="text"
              class="w-full pl-3 py-2 border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12"
              :placeholder="t('PIPELINE.EDIT_STAGE_MODAL.NAME_PLACEHOLDER')"
            />
          </div>

          <div>
            <!-- ColorPicker é botão custom (não-labelable): span + group
                 aria-labelledby no lugar de <label> órfão. -->
            <span
              :id="fieldId('stage-color-label')"
              class="block text-sm font-medium mb-1"
              >{{ t('PIPELINE.EDIT_STAGE_MODAL.COLOR') }}</span
            >
            <div role="group" :aria-labelledby="fieldId('stage-color-label')">
              <StageColorPicker v-model="editingStageForm.color" />
            </div>
          </div>

          <div>
            <label
              :for="fieldId('stage-description')"
              class="block text-sm font-medium mb-1"
              >{{ t('PIPELINE.EDIT_STAGE_MODAL.DESCRIPTION') }}</label
            >
            <textarea
              :id="fieldId('stage-description')"
              v-model="editingStageForm.description"
              name="stage_description"
              class="w-full pl-3 py-2 border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12"
              rows="3"
              :placeholder="
                t('PIPELINE.EDIT_STAGE_MODAL.DESCRIPTION_PLACEHOLDER')
              "
            />
          </div>

          <div class="flex justify-end gap-2">
            <button variant="clear" @click="showEditStageModal = false">
              {{ t('PIPELINE.ACTIONS.CANCEL') }}
            </button>
            <button variant="primary" @click="handleSaveStage">
              {{ t('PIPELINE.ACTIONS.SAVE') }}
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de Ordenação -->
    <Modal v-model:show="showSortModal" @close="() => (showSortModal = false)">
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.SORT_MODAL.TITLE') }}
        </h3>

        <div class="space-y-4">
          <div>
            <label
              :for="fieldId('sort-type')"
              class="block text-sm font-medium mb-2"
              >{{ t('PIPELINE.SORT_MODAL.SORT_BY') }}</label
            >
            <select
              :id="fieldId('sort-type')"
              v-model="columnSort.type"
              name="sort_type"
              class="w-full pl-3 py-2 border border-n-weak rounded-lg bg-n-solid-2 text-n-slate-12"
              @change="saveSortSettings"
            >
              <option
                v-for="option in sortOptions"
                :key="option.id"
                :value="option.id"
              >
                {{
                  t(
                    `PIPELINE.SORT_OPTIONS.${option.id.toUpperCase()}`,
                    option.label
                  )
                }}
              </option>
            </select>
          </div>

          <div>
            <!-- Grupo de radios: span + radiogroup aria-labelledby no lugar
                 de <label> órfão (não há controle único para associar). -->
            <span
              :id="fieldId('sort-direction-label')"
              class="block text-sm font-medium mb-2"
              >{{ t('PIPELINE.SORT_MODAL.DIRECTION') }}</span
            >
            <div
              role="radiogroup"
              :aria-labelledby="fieldId('sort-direction-label')"
              class="flex gap-4"
            >
              <label class="flex items-center gap-2">
                <input
                  :id="fieldId('sort-direction-asc')"
                  v-model="columnSort.direction"
                  :name="fieldId('sort-direction')"
                  type="radio"
                  value="asc"
                  @change="saveSortSettings"
                />
                <span class="text-sm">{{
                  t('PIPELINE.SORT_MODAL.ASCENDING')
                }}</span>
              </label>
              <label class="flex items-center gap-2">
                <input
                  :id="fieldId('sort-direction-desc')"
                  v-model="columnSort.direction"
                  :name="fieldId('sort-direction')"
                  type="radio"
                  value="desc"
                  @change="saveSortSettings"
                />
                <span class="text-sm">{{
                  t('PIPELINE.SORT_MODAL.DESCENDING')
                }}</span>
              </label>
            </div>
          </div>

          <div class="flex justify-end gap-2">
            <button variant="clear" @click="showSortModal = false">
              {{ t('PIPELINE.FORM.CANCEL') }}
            </button>
            <button variant="primary" @click="showSortModal = false">
              {{ t('PIPELINE.FORM.APPLY') }}
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Deal status: marcar Ganho/Perdido a partir do dropdown inline do card.
         Os modais capturam won_value/motivo (alguns funis exigem) e chamam os
         endpoints de deal_status; o sucesso aplica o payload no store. -->
    <MarkAsWonModal
      v-if="dealStatusCard"
      :item="dealStatusCard"
      :show="showWonModal"
      @close="showWonModal = false"
      @success="handleDealStatusSuccess"
    />
    <MarkAsLostModal
      v-if="dealStatusCard"
      :item="dealStatusCard"
      :show="showLostModal"
      @close="showLostModal = false"
      @success="handleDealStatusSuccess"
    />
  </section>
</template>

<!-- CSS migrated to Tailwind classes -->
